import React, {useEffect, useMemo, useRef} from 'react';
import { useSelector } from 'react-redux';
import Hls from 'hls.js';
import {GreenroomState, GreenroomState as GS} from '../stores/types/types';
import { STREAMING_TYPES, VID_TYPES } from '../types/constants';
import { isVideo } from './utils';

/**
 * Created to preload the next video in the show, so that it is ready to play when the current shot ends.
 * We were running into an issue where bullman was delaying playing a video when switching shots, we are
 * hoping this will fix that issue.
 */
export default function PreloadVideo() {
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);
	const graphics = useSelector((state: GreenroomState) => state.GraphicsReducer.graphics);

	const currentShot = useMemo(() => {
		if (currentShotId) {
			return shots.find(s => s.shot_id === currentShotId);
		}
		return shots?.[0];
	}, [currentShotId, shots]);

	const nextShot = useMemo(() => {
		if (!currentShot) return;

		const currentShotIndex = shots.findIndex(s => s.shot_id === currentShot?.shot_id);
		if (currentShotIndex >= 0 && currentShotIndex < shots.length - 1) {
			return shots[currentShotIndex + 1];
		} else if (currentShotIndex < 0) {
			return shots[0];
		}
		return shots?.[currentShotIndex];

	}, [currentShot, shots]);

	// Preload media videos assigned to the next shot's feeds
	useEffect(() => {
		if (currentShot && nextShot && currentShot.shot_id !== nextShot.shot_id) {
			const shotMedia = nextShot.shot_data?.shotFeeds.filter(f => f.feedType === 'media');
			const firstShotFeedWithVideo = shotMedia?.find((sf) => media.find(
				m => m.id === sf.media && VID_TYPES.includes(m.type),
			));
			if (firstShotFeedWithVideo) {
				const mediaItem = media.find(m => m.id === firstShotFeedWithVideo.media);

				if (mediaItem?.images) {
					console.log(`Preloading video ${mediaItem?.images}`);

					const request = new XMLHttpRequest();
					request.open('GET', mediaItem.images, true);
					request.responseType = 'blob';
					request.onload = () => {
						if (request.status === 200) {
							const videoBlob = request.response;
							const videoUrl = URL.createObjectURL(videoBlob);
							const video = document.createElement('video');
							video.src = videoUrl;
							video.preload = 'auto';
							video.load();

							console.log(`Completed preloading video ${mediaItem?.images}`);
						}
					}
					request.onerror = () => {
						console.log(`Error pre loading video ${mediaItem?.images}`);
					}
					request.send();
				}
			}
		}
	}, [nextShot, currentShot, media]);

	// Preload video overlays assigned to the next shot
	const preloadedOverlayUrls = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (currentShot && nextShot && currentShot.shot_id !== nextShot.shot_id) {
			const shotGraphicIds = nextShot.shot_data?.shotGraphics ?? [];
			shotGraphicIds.forEach((graphicId: number) => {
				const graphic = graphics.find(g => g.id === graphicId);
				if (!graphic?.images) return;
				if (!isVideo(graphic.images)) return;

				// Skip if this URL has already been preloaded
				if (preloadedOverlayUrls.current.has(graphic.images)) return;
				preloadedOverlayUrls.current.add(graphic.images);

				const extension = graphic.images.split('.').pop()?.toLowerCase() ?? '';
				const isStreaming = STREAMING_TYPES.includes(extension);

				if (isStreaming) {
					// HLS: use hls.js to fetch manifest + buffer segments into cache
					console.log(`Preloading HLS overlay video ${graphic.images}`);
					const video = document.createElement('video');
					const hls = new Hls();
					hls.loadSource(graphic.images);
					hls.attachMedia(video);
					hls.on(Hls.Events.ERROR, (_event, data) => {
						console.log(`Error preloading HLS overlay video ${graphic.images}`, data.type);
					});
				} else {
					// Standard video: XHR blob to warm HTTP cache
					console.log(`Preloading overlay video ${graphic.images}`);
					const request = new XMLHttpRequest();
					request.open('GET', graphic.images, true);
					request.withCredentials = false; // match crossOrigin="anonymous" used by the overlay video element
					request.responseType = 'blob';
					request.onload = () => {
						if (request.status === 200) {
							const videoBlob = request.response;
							const videoUrl = URL.createObjectURL(videoBlob);
							const video = document.createElement('video');
							video.src = videoUrl;
							video.preload = 'auto';
							video.load();

							console.log(`Completed preloading overlay video ${graphic.images}`);
						}
					};
					request.onerror = () => {
						console.log(`Error preloading overlay video ${graphic.images}`);
					};
					request.send();
				}
			});
		}
	}, [nextShot, currentShot, graphics]);

	return (<></>);
}